// requestStore.js
import { makeAutoObservable, runInAction } from 'mobx'
import axios from 'axios'
import sessionStore from './sessionStore'
import objectStore from './objectStore'
import filterStore from './filterStore'
import ExtraFilters from '../components/object/ExtraFilters'
import { t } from 'i18next'
import { config } from '../config'
import logger from '../utils/logger'
import apiClient from '../utils/apiClient'
import i18nStore from './i18nStore'
import { normalizeLinkedObject } from '../utils/requestObjectAvailability'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import resourceAssignmentApi from '../tenancy/resourceAssignmentApi'

export class RequestStore {
	requests = [] // Список заявок
	loading = false // Флаг загрузки
	error = null // Для хранения ошибки, если нужно
	activeRequestObjects = [] // объекты заявки
	activeRequestObjectsAvailable = [] // объекты заявки, доступные для добавления
	suitedObjectForRequest = []
	notSuitedObjectForRequest = []
	/* statusForNewRequest = null */

	filterForRequest = {
		saleType: 'sale',
		activeCategoryId: null, // Активная категория
		selectedSubcategories: [], // Выбранные подкатегории
		rentType: [], // Массив значений, например ['longTerm', 'daily']
	}

	address = {}
	isAddressSelected = false

	extraFilter = {} // Дополнительные фильтры

	// Добавляем свойства для пагинации доступных объектов
	currentPageAvailable = 1
	totalPagesAvailable = 1
	nextRequestMoveVersion = 0
	pendingRequestMoves = new Map()

	constructor() {
		makeAutoObservable(this)
	}

	clearOrganizationState() {
		this.requests = []
		this.loading = false
		this.error = null
		this.activeRequestObjects = []
		this.activeRequestObjectsAvailable = []
		this.suitedObjectForRequest = []
		this.notSuitedObjectForRequest = []
		this.filterForRequest = {
			saleType: 'sale', activeCategoryId: null, selectedSubcategories: [], rentType: [],
		}
		this.address = {}
		this.isAddressSelected = false
		this.extraFilter = {}
		this.currentPageAvailable = 1
		this.totalPagesAvailable = 1
		this.activeBoardId = null
		this.nextRequestMoveVersion += 1
		this.pendingRequestMoves.clear()
	}

	setAddress(adr) {
		this.address = adr
		this.isAddressSelected = true // устанавливаем флаг при выборе адреса из dadata
	}

	resetAddressSelection() {
		this.isAddressSelected = false
	}

	clearAddress() {
		this.address = {}
		this.isAddressSelected = false
	}

	clearRequestStoreFilter() {
		this.extraFilter = {}
		this.address = {}
	}

	setActiveCategoryId(id) {
		this.filterForRequest.activeCategoryId = id
		this.filterForRequest.selectedSubcategories = [] // Сброс подкатегорий
	}

	toggleSubcategory(subcategory) {
		const index = this.filterForRequest.selectedSubcategories.findIndex(
			sub => sub.id === subcategory.id
		)
		if (index > -1) {
			// Удаляем подкатегорию
			this.filterForRequest.selectedSubcategories.splice(index, 1)
		} else {
			// Добавляем подкатегорию
			this.filterForRequest.selectedSubcategories.push(subcategory)
		}
	}


	// добавляем доску
	async addBoard(boardName) {
		logger.log('🎈 ENDP-10 addBoard')
		logger.log(`Attempting to add new board with name: ${boardName}`);

		// Устанавливаем флаги загрузки и сбрасываем ошибку
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// Используем apiClient.post
			// Отправляем только boardName. ID текущего пользователя бэкенд возьмет из req.user.id
			const response = await apiClient.post('/boards', {
				boardName: boardName // Отправляем только имя доски
			});

			// The backend derives the selected Organization and actor from its request context.
			// 4. Вернуть данные созданной доски (как минимум id и sort).
			// ****************************

			// Данные в response.data (ожидаем объект с id и sort созданной доски)
			const newBoardData = response.data;
			logger.log('Board added successfully on server:', newBoardData);

			// Board creation also creates its system columns, so the server is the
			// only canonical source for the resulting board shape.
			await this.fetchRequests('board-created')
			return newBoardData

		} catch (error) {
			const errorMsg = error.response?.data?.error || error.message || 'Ошибка при добавлении доски';
			logger.error('Error adding board:', errorMsg);
			runInAction(() => {
				// Сохраняем сообщение об ошибке
				this.error = errorMsg;
			});
			// Можно пробросить ошибку дальше, если нужно
			// throw new Error(errorMsg);
		} finally {
			// Всегда убираем флаг загрузки
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	// переименование доски
	async renameBoard(boardId, newName) {
		logger.log(`🎈 ENDP-11 Переименование доски board ${boardId} to "${newName}"`);

		// Находим текущую доску в сторе, чтобы можно было откатить имя при ошибке (опционально)
		const boardIndex = this.requests.findIndex(req => req.boardId === boardId);
		if (boardIndex === -1) {
			logger.error(`Cannot rename board: Board with ID ${boardId} not found locally.`);
			// Устанавливаем ошибку
			runInAction(() => {
				this.error = `Доска с ID ${boardId} не найдена для переименования.`;
			});
			return; // Выходим, если доски нет локально
		}
		const originalName = this.requests[boardIndex].name;

		// Оптимистичное обновление (опционально, но улучшает UX)
		// Сразу меняем имя в UI, а если сервер вернет ошибку - откатываем
		/*
		runInAction(() => {
			this.requests[boardIndex].name = newName;
		});
		*/

		// Устанавливаем флаги загрузки и сбрасываем ошибку
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// Используем apiClient.put
			// Отправляем только newName. Бэкенд должен проверить права доступа пользователя (из req.user.id) к этой доске.
			const response = await apiClient.put(`/boards/${boardId}`, {
				name: newName // Отправляем только новое имя
			});

			// Данные в response.data (ожидаем обновленный объект доски)
			const updatedBoardData = response.data.board; // Предполагаем, что ответ { board: { id, name, ... } }
			logger.log('Board renamed successfully on server:', updatedBoardData);

			// Обновляем состояние MobX внутри runInAction (если не было оптимистичного обновления)
			// Если было оптимистичное, то здесь можно просто убедиться, что серверное имя совпадает
			runInAction(() => {
				// Находим доску еще раз (или используем boardIndex)
				const boardToUpdate = this.requests.find(req => req.boardId === boardId);
				if (boardToUpdate) {
					// Обновляем имя на то, что вернул сервер (самый надежный вариант)
					boardToUpdate.name = updatedBoardData.name;
					logger.log(`Local board ${boardId} name updated to "${updatedBoardData.name}" based on server response.`);
				}
			});

		} catch (error) {
			const errorMsg = error.response?.data?.error || error.message || 'Ошибка при переименовании доски';
			logger.error('Error renaming board:', errorMsg);

			// Откатываем оптимистичное обновление, если оно было

			runInAction(() => {
				const boardToRevert = this.requests.find(req => req.boardId === boardId);
				if (boardToRevert) {
					boardToRevert.name = originalName; // Возвращаем старое имя
					logger.warn(`Reverted board ${boardId} name back to "${originalName}" due to error.`);
				}
			});


			runInAction(() => {
				// Сохраняем сообщение об ошибке
				this.error = errorMsg;
			});
			// Можно пробросить ошибку дальше
			// throw new Error(errorMsg);
		} finally {
			// Всегда убираем флаг загрузки
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	async getBoardDeletionPlan(boardId) {
		const response = await apiClient.get(`/boards/${boardId}/deletion-plan`)
		return response.data
	}

	async migrateAndDeleteBoard(boardId, mappings) {
		try {
			const response = await apiClient.post(`/boards/${boardId}/migrate-and-delete`, { mappings })
			await this.fetchRequests('board-migrated-and-deleted')
			return response.data
		} catch (error) {
			await this.fetchRequests('board-migration-failed')
			throw error
		}
	}

	// удаляем доску
	async deleteBoard(boardId) {
		logger.log(`🎈 ENDP-12 Удаляем доску board with ID: ${boardId}`);

		// Находим индекс доски для возможного восстановления при ошибке (опционально)
		const boardIndex = this.requests.findIndex(req => req.boardId === boardId);
		if (boardIndex === -1) {
			logger.warn(`Cannot delete board: Board with ID ${boardId} not found locally.`);
			// Можно просто выйти или установить ошибку
			runInAction(() => {
				this.error = `Доска с ID ${boardId} не найдена для удаления.`;
			});
			return;
		}
		// Оптимистичное удаление (опционально, но улучшает UX)
		// Сразу удаляем из UI, а если сервер вернет ошибку - возвращаем
		/*
		runInAction(() => {
			this.requests.splice(boardIndex, 1);
			logger.log(`Optimistically removed board ${boardId} from local store.`);
		});
		*/

		// Устанавливаем флаги загрузки и сбрасываем ошибку
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// Используем apiClient.delete
			// Заголовки и тело не нужны, ID передается в URL.
			// Права доступа проверит бэкенд на основе токена.
			const response = await apiClient.delete(`/boards/${boardId}`);

			logger.log(`Board ${boardId} deleted successfully on server.`);

			// Обновляем состояние MobX внутри runInAction (если не было оптимистичного удаления)
			runInAction(() => {
				// Фильтруем массив, удаляя доску
				this.requests = this.requests.filter(req => req.boardId !== boardId);
				logger.log(`Removed board ${boardId} from local store after server confirmation.`);
			});
			return { deleted: true, data: response.data }

		} catch (error) {
			const errorMsg = error.response?.data?.error || error.message || 'Ошибка при удалении доски';
			logger.error('Error deleting board:', errorMsg);

			// Откатываем оптимистичное удаление, если оно было
			/*
			runInAction(() => {
				// Возвращаем доску на ее прежнее место
				this.requests.splice(boardIndex, 0, boardToRemove);
				this.requests.sort((a, b) => a.boardSort - b.boardSort); // Восстанавливаем сортировку, если она есть
				logger.warn(`Reverted optimistic removal of board ${boardId} due to error.`);
			});
			*/

			runInAction(() => {
				// Сохраняем сообщение об ошибке
				this.error = errorMsg;
			});
			if (error.response?.status === 409 && error.response?.data?.plan) {
				return { deleted: false, plan: error.response.data.plan }
			}
			throw error
		} finally {
			// Всегда убираем флаг загрузки
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	// сохраняем новую заявку
	async createRequest(boardId, columnId, title, description, addressShort, sourceId = null, clientSelection = null) {
		logger.log(`🌟 jfu48dk  Сохраняем заявку на доске: ${boardId}, Column: ${columnId}`);

		// Проверка наличия пользователя (не для отправки, а для уверенности в контексте)
		if (!sessionStore.user?.id) {
			logger.error('Cannot create request: User not authenticated.');
			runInAction(() => {
				this.error = 'Пользователь не авторизован для создания заявки.';
			});
			throw new Error('Пользователь не авторизован');
		}

		// Устанавливаем флаги загрузки и сбрасываем ошибку
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		// Получаем ID объектов из objectStore
		const selectedObjects = objectStore.objectsForRequest.map(obj => obj.id);

		try {
			const payload = {
				boardId,
				columnId,
				title,
				description,
				objects: selectedObjects, // ID объектов из objectStore
				suitedObjects: this.suitedObjectForRequest,
				notSuitedObjects: this.notSuitedObjectForRequest,
				commonFilter: this.filterForRequest,
				extraFilters: this.extraFilter,
				address: this.address,
				addressShort: addressShort, // Добавляем короткий адрес в payload
				sourceId,
				...(clientSelection?.clientId ? { clientId: clientSelection.clientId } : {}),
				...(clientSelection?.newClient ? { newClient: clientSelection.newClient } : {}),
				countryCodeISO: i18nStore.currentCountry
			};
			logger.log('Sending request payload:', payload); // Логируем то, что отправляем

			const response = await apiClient.post('/requests', payload);
			// Данные в response.data (ожидаем объект созданной заявки)
			const newRequestData = response.data;
			logger.log('Request created successfully on server:', newRequestData);

			// Добавляем заявку в локальный стор с помощью хелпера
			// Важно: Убедись, что addRequestToLocalStore корректно обрабатывает newRequestData
			this.addRequestToLocalStore(boardId, columnId, newRequestData);

			return { success: true, data: newRequestData }; // Возвращаем данные созданной заявки с флагом успешности

		} catch (error) {
			const errorMsg = error.response?.data?.message || error.message || 'Ошибка при создании заявки';
			logger.error('Error creating request:', error);

			runInAction(() => {
				// Сохраняем сообщение об ошибке
				this.error = errorMsg;
			});

			// Если ошибка связана с превышением лимита (код 422)
			if (error.response && error.response.status === 422) {
				const errorData = error.response.data;
				return {
					success: false,
					error: errorData.message,
					currentCount: errorData.currentCount,
					limit: errorData.limit
				};
			}

			// Для других ошибок
			return {
				success: false,
				error: errorMsg
			};
		} finally {
			// Всегда убираем флаг загрузки
			runInAction(() => {
				this.loading = false;
			});

		}
	}

	// Метод для получения всех заявок
	async fetchRequests(source = 'unknown') { // Добавил source для логирования
		logger.log(`Fetching all requests/boards for the current user. Source: ${source}`);

		// Устанавливаем флаги загрузки и сбрасываем ошибку
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// The selected Organization is derived by apiClient and enforced by the backend.
			const response = await apiClient.get('/requests');

			// The server returns Organization-scoped Board and Request DTOs.
			// 5. Собрать все это в структурированный ответ (например, массив досок, каждая с массивом колонок, каждая с массивом заявок).
			// ****************************

			// Данные в response.data (ожидаем массив досок со всей структурой)
			const boardsData = response.data;
			logger.log('Requests/boards data received successfully:', boardsData);

			// Обновляем состояние MobX внутри runInAction
			runInAction(() => {
				// Присваиваем полученные данные или пустой массив, если ответ некорректный
				this.requests = Array.isArray(boardsData) ? boardsData : [];
				this.loading = false;
			});

		} catch (error) {
			const errorMsg = error.response?.data?.error || error.message || 'Ошибка при получении заявок';
			logger.error('Error fetching requests:', errorMsg);
			runInAction(() => {
				this.loading = false;
				// Сохраняем сообщение об ошибке
				this.error = errorMsg;
				this.requests = []; // Очищаем данные при ошибке
			});
			// Можно пробросить ошибку, если это нужно для UI
			// throw new Error(errorMsg);
		}
		// finally блок не нужен, т.к. loading = false ставится и в try, и в catch
	}

	async addColumn(boardId, columnName) {
		logger.log(`Attempting to add column "${columnName}" to board ${boardId}`);

		// Устанавливаем флаги загрузки и сбрасываем ошибку
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// Используем apiClient.post
			// Отправляем boardId и columnName. Бэкенд проверит права на boardId.
			const response = await apiClient.post('/columns', {
				boardId: boardId,
				name: columnName
			});

			// The route authorizes Board structural changes from server-side Organization policy.
			// ****************************

			// Данные в response.data (ожидаем объект созданной колонки)
			const newColumnData = response.data;
			logger.log('Column added successfully on server:', newColumnData);

			// Обновляем состояние MobX внутри runInAction
			runInAction(() => {
				// Находим доску в локальном сторе
				const board = this.requests.find(b => b.boardId === boardId);

				if (board) {
					// Добавляем новую колонку в массив columns этой доски
					// Используем columnId, как было в оригинале!
					const newColumnLocal = {
						columnId: newColumnData.id, // <-- ВОЗВРАЩАЕМ columnId, используя id из ответа сервера
						name: newColumnData.name,
						sort: newColumnData.sort,
						colorToken: newColumnData.colorToken,
						systemType: newColumnData.systemType,
						requests: [], // Инициализируем пустой массив для заявок
					};
					board.columns = board.columns || []; // Убедимся, что массив существует
					board.columns.push(newColumnLocal);
					// Возможно, нужна сортировка board.columns по newColumnLocal.sort
					// board.columns.sort((a, b) => a.sort - b.sort);
					logger.log(`Added new column ${newColumnLocal.columnId} to local board ${boardId}.`);
				} else {
					logger.error(`Cannot add column locally: Board ${boardId} not found in store, though server responded successfully.`);
					this.error = `Не удалось обновить интерфейс: доска ${boardId} не найдена. Обновите страницу.`;
				}
			});

		} catch (error) {
			const errorMsg = error.response?.data?.error || error.message || 'Ошибка при добавлении колонки';
			logger.error('Error adding column:', errorMsg);
			runInAction(() => {
				// Сохраняем сообщение об ошибке
				this.error = errorMsg;
			});
			// Можно пробросить ошибку дальше
			// throw new Error(errorMsg);
		} finally {
			// Всегда убираем флаг загрузки
			runInAction(() => {
				this.loading = false;
			});
		}
	}
	// Функция для переименования колонки
	renameColumn = async (columnId, newName, boardId) => {
		logger.log('🎈 ENDP-16 - renameColumn')
		logger.log(`columnId: ${columnId}`) // id колонки
		logger.log(`newName: ${newName}`) // новое название
		logger.log(`boardId: ${boardId}`) // id доски

		const board = this.getBoardById(boardId)
		logger.log(`board: ${board}`)

		if (!board) {
			logger.error(`Доска с boardId ${boardId} не найдена`)
			return
		}

		try {
			this.loading = true
			this.error = null

			// Отправляем запрос на сервер через apiClient
			const response = await apiClient.post('/renameColumn', {
				columnId,
				newName,
				boardId,
			})

			// Проверяем ответ сервера
			if (response.status === 200) {
				// Обновляем локальный стор
				runInAction(() => {
					this.updateColumnName(board, columnId, newName)
				})
				logger.log('Колонка успешно переименована')
			} else {
				logger.error('Ошибка при переименовании колонки:', response.data)
				runInAction(() => {
					this.error = 'Ошибка при переименовании колонки'
				})
			}
		} catch (err) {
			logger.error('Ошибка при выполнении запроса:', err)
			runInAction(() => {
				this.error = 'Ошибка при выполнении запроса'
			})
		} finally {
			runInAction(() => {
				this.loading = false
			})
		}
	}

	//------------------------------------------------------------------------------------
	//------------------------------------------------------------------------------------
	//------------------------------------------------------------------------------------


	async deleteObjectFromActiveRequest(objectId, requestId) {
		logger.log('🎈 ENDP-27 - deleteObjectFromActiveRequest')
		logger.log(`Удаление объекта из заявки ${objectId}`)

		try {
			const response = await apiClient.delete(
				`/requests/${requestId}/objects/${objectId}`
			)
			logger.log(`Объект ${objectId} успешно удален из заявки ${requestId}`)

			// Находим объект перед удалением, чтобы потом добавить его в доступные
			const deletedObject = this.activeRequestObjects.find(obj => obj.id === objectId)

			// Удаляем объект из локального массива activeRequestObjects
			this.activeRequestObjects = this.activeRequestObjects.filter(obj => obj.id !== objectId)

			// Если объект найден, добавляем его в начало списка доступных объектов
			if (deletedObject && deletedObject.availability !== 'UNAVAILABLE') {
				// Создаем копию объекта без статуса и requestId, так как это теперь доступный объект
				const availableObject = { ...deletedObject }
				delete availableObject.status;
				delete availableObject.requestId;

				// Добавляем в начало массива доступных объектов
				this.activeRequestObjectsAvailable.unshift(availableObject)
				logger.log(`Объект ${objectId} добавлен в список доступных объектов`)
			}

			return response.data
		} catch (error) {
			logger.error('Ошибка при удалении объекта из заявки:', error)
			throw error
		}
	}

	async setNewStatusOnAvailableObject(requestId, objectId, newStatus) {
		logger.log('🎈 ENDP-23 - setNewStatusOnAvailableObject')
		logger.log(`Изменение статуса доступного объекта ${objectId} на ${newStatus} у заявки ${requestId}`)

		try {
			const response = await apiClient.patch(
				`/requests/update-object-status/${objectId}`,
				{
					requestId: requestId,
					newStatus: newStatus,
				}
			)
			logger.log(`Статус объекта ${objectId} успешно обновлен:`, response.data)

			// Находим и удаляем объект из activeRequestObjectsAvailable
			const availableIndex = this.activeRequestObjectsAvailable.findIndex(
				obj => obj.id === objectId
			)
			logger.log(`😍😍😍 availableIndex: ${availableIndex}`)

			if (availableIndex !== -1) {
				// Вырезаем объект из activeRequestObjectsAvailable
				const [removedObject] = this.activeRequestObjectsAvailable.splice(
					availableIndex,
					1
				)

				// Добавляем объект в activeRequestObjects с добавлением status и requestId
				this.activeRequestObjects.push({
					...removedObject, // Сохраняем все существующие поля объекта
					status: newStatus, // Добавляем новое значение status
					requestId: requestId, // Добавляем requestId
				})
			} else {
				logger.warn(
					`Объект с objectId ${objectId} не найден в activeRequestObjectsAvailable`
				)
				// Если объекта нет в available, можно либо ничего не делать, либо добавить новый
				this.activeRequestObjects.push({
					objectId,
					status: newStatus,
					requestId: requestId,
				})
			}
		} catch (error) {
			logger.error('Ошибка при обновлении статуса объекта:', error)
			throw error // Пробрасываем ошибку для обработки на фронте
		}
	}


	deleteObjectFromRequest = async objectId => {
		logger.log(`Удаляем объект из заявки: ${objectId}`)

		const index = objectStore.objectsForRequest.findIndex(
			obj => obj.id === objectId
		)
		logger.log(`Индекс объекта: ${index}`)
		if (index !== -1) {
			objectStore.objectsForRequest.splice(index, 1)
		}
	}


	// обновление статуса объекта
	async updateObjectStatus(requestId, objectId, newStatus) {
		logger.log('🎈 ENDP-22 - updateObjectStatus')
		logger.log(`Изменение статуса объекта ${objectId} на ${newStatus} для заявки ${requestId}`)

		try {
			const response = await apiClient.patch(
				`/requests/${requestId}/objects/${objectId}`,
				{
					newStatus: newStatus,
				}
			)
			logger.log(`Статус объекта ${objectId} успешно обновлен:`, response.data)

			// Обновляем локальные данные в activeRequestObjects
			const updatedObjectIndex = this.activeRequestObjects.findIndex(
				obj => obj.id === objectId
			)
			if (updatedObjectIndex !== -1) {
				this.activeRequestObjects[updatedObjectIndex].status = newStatus
			}
		} catch (error) {
			logger.error('Ошибка при обновлении статуса объекта:', error)
			throw error // Пробрасываем ошибку для обработки на фронте, если нужно
		}
	}

	// Метод для перемещения колонки влево
	async moveColumnLeft(boardId, columnId) {
		const board = this.requests.find(board => board.boardId === boardId)
		const ordered = board ? this.sortedColumns(board) : []
		const index = ordered.findIndex(column => column.columnId === columnId)
		if (index > 0) await this.updateColumnOrder(boardId, index, index - 1)
	}

	async assignRequest(requestId, assigneeMembershipId) {
		// Dedicated assignment changes only current handling. It never changes
		// criteria and therefore cannot trigger a semantic matching request.
		const assignment = await resourceAssignmentApi.assign('requests', requestId, assigneeMembershipId)
		await this.fetchRequests('request-assignment')
		return assignment
	}

	// Метод для перемещения колонки вправо
	async moveColumnRight(boardId, columnId) {
		const board = this.requests.find(board => board.boardId === boardId)
		const ordered = board ? this.sortedColumns(board) : []
		const index = ordered.findIndex(column => column.columnId === columnId)
		if (index >= 0 && index < ordered.length - 1) await this.updateColumnOrder(boardId, index, index + 1)
	}

	// Функция для получения объектов заявки
	async fetchRequestObjects(requestId, boardId, columnId) {
		logger.log('🎈 ENDP-24 - fetchRequestObjects')
		logger.log(`Получение объектов для заявки: requestId=${requestId}, boardId=${boardId}, columnId=${columnId}`)

		try {
			const response = await apiClient.get(`/requests/${requestId}/objects`)
			logger.log('Объекты заявки:', response.data)

			// Очищаем массив и записываем новые данные
			this.activeRequestObjects = [] // Очистка массива
			logger.log('😎 Объекты заявки:', response.data)

			// Нормализуем данные перед записью в store
			const normalizedObjects = (response.data.objects || []).map(normalizeLinkedObject)
			const normalizedAvailableObjects = (response.data.availableObjects || []).map(normalizeLinkedObject)

			this.activeRequestObjects = normalizedObjects
			this.activeRequestObjectsAvailable = normalizedAvailableObjects
		} catch (error) {
			logger.error('Ошибка при получении объектов заявки:', error)
			runInAction(() => {
				this.activeRequestObjects = []
				this.activeRequestObjectsAvailable = []
			})
		}
	}

	async updateColumnColor(columnId, colorToken) {
		logger.log('🎈 ENDP-25 - updateColumnColor')
		logger.log(`colorToken: ${colorToken}`)

		try {
			// Отправляем запрос на сервер для обновления цвета колонки
			const response = await apiClient.put(`/columns/${columnId}/color`, { colorToken })

			// Обновляем цвет колонки в локальном сторе
			runInAction(() => {
				const column = this.requests
					.flatMap(board => board.columns)
					.find(col => col.columnId === columnId)

				if (column) {
					//	column.color = color
					column.colorToken = response.data.colorToken
				} else {
					logger.error(`Колонка с columnId ${columnId} не найдена`)
				}
			})

			return response.data
		} catch (error) {
			logger.error('Ошибка при обновлении цвета колонки:', error)
			runInAction(() => {
				this.error = error
			})
			throw error
		}
	}

	async updateRequestColor(id, colorToken) {
		logger.log('🎈 ENDP-26 - updateRequestColor')
		logger.log(id, colorToken)

		try {
			// Отправляем запрос на сервер для обновления цветов
			const response = await apiClient.put(`/requests/${id}/color`, { colorToken })

			// Обновляем локальный стор
			runInAction(() => {
				const request = this.requests
					.flatMap(board => board.columns.flatMap(col => col.requests))
					.find(req => req.requestId === id)

				if (request) {
					request.colorToken = response.data.colorToken
					logger.log(`Цвета заявки ${id} обновлены локально`)
				} else {
					logger.error(`Заявка с id ${id} не найдена`)
				}
			})

			return response.data
		} catch (error) {
			logger.error('Ошибка при обновлении цветов заявки:', error)
			runInAction(() => {
				this.error = error
			})
			throw error
		}
	}

	async syncColumnOrderWithServer(boardId, columns) {
		await apiClient.post('/update-column-order', {
			boardId,
			columns: columns.map(column => ({ columnId: column.columnId, sort: column.sort })),
		})
	}



	// удаление колонки
	removeColumn = async (boardId, columnId) => {
		logger.log('🎈 ENDP-20 - removeColumn удаление колонки')
		logger.log(`Удаляем колонку ${columnId}`)

		try {
			// Находим доску по boardId
			const board = this.requests.find(b => b.boardId === boardId)

			if (!board) {
				throw new Error('Доска не найдена')
			}

			// Находим колонку, которую собираемся удалить
			const columnToDelete = board.columns.find(
				col => col.columnId === columnId
			)

			if (!columnToDelete) {
				throw new Error('Колонка не найдена')
			}
			if (columnToDelete.systemType !== 'NORMAL' || columnToDelete.requests.length > 0) {
				throw new Error('Можно удалить только пустую обычную колонку')
			}

			// Сохраняем sort удаляемой колонки
			const deletedColumnSort = columnToDelete.sort

			// Отправляем запрос на сервер для удаления колонки
			const response = await apiClient.delete(`/columns/${columnId}`)

			if (response.status === 200) {
				runInAction(() => {
					// Удаляем колонку из доски
					board.columns = board.columns.filter(col => col.columnId !== columnId)

					// Пересортировка оставшихся колонок
					board.columns.forEach(col => {
						if (col.sort > deletedColumnSort) {
							col.sort -= 1
						}
					})

					logger.log(`Колонка ${columnId} удалена`)
				})
			}
		} catch (error) {
			await this.fetchRequests('column-delete-failed')
			runInAction(() => {
				logger.log('ошибка удаления', error) // Дополнительный лог ошибки
				this.error = error.message || 'Ошибка при удалении колонки'
			})
		} finally {
			runInAction(() => {
				this.loading = false // Завершаем процесс загрузки
			})
		}
	}



	// сортировка доски
	moveBoard = async (boardId, direction) => {
		logger.log('🎈 ENDP-21 - moveBoard сортировка доски')
		this.loading = true // Устанавливаем флаг загрузки
		this.error = null // Сбрасываем ошибки

		try {
			// Отправляем запрос на сервер для изменения сортировки
			const response = await apiClient.put(`/boards/sort`, {
				boardId,
				direction,
			})

			const updatedBoards = response.data.updatedBoards

			logger.log(`💙 updatedBoards: ${JSON.stringify(updatedBoards)}`)

			runInAction(() => {
				// Обновляем локальные данные сортировки
				updatedBoards.forEach(({ id, sort }) => {
					this.requests = this.requests.map(request =>
						request.boardId === id ? { ...request, sort: sort } : request
					)
				})
			})
		} catch (error) {
			runInAction(() => {
				this.error = error.message || 'Ошибка при изменении сортировки'
			})
		} finally {
			runInAction(() => {
				this.loading = false // Завершаем процесс загрузки
			})
		}
	}



	updateSelectedFields() {
		const selectedSubcategories = this.filterForRequest.selectedSubcategories

		logger.log('🎀 selectedSubcategories:', selectedSubcategories)

		if (!selectedSubcategories || selectedSubcategories.length === 0) {
			// Не сбрасываем фильтр, а просто выходим
			return
		}

		const fieldSets = selectedSubcategories.map(subcategory => {
			logger.log('🎇 subcategory.id:', subcategory.id)

			// Поиск JSON-файла с проверкой наличия type.id
			const jsonFile = objectStore.jsonfiles.find(
				file => file.type && file.type.id === subcategory.id
			)

			logger.log('🎀 jsonFile:', jsonFile)

			return jsonFile && jsonFile.fields ? Object.keys(jsonFile.fields) : []
		})

		logger.log('🎀 fieldSets:', fieldSets)

		if (fieldSets.length === 0 || fieldSets.some(set => set.length === 0)) {
			// Не сбрасываем фильтр, а просто выходим
			return
		}

		// Пересекаем ключи всех полей
		const commonFields = fieldSets.reduce((common, fieldSet) =>
			common.filter(field => fieldSet.includes(field))
		)

		logger.log('🎀 commonFields:', commonFields)

		// Создаем объект с описанием общих полей
		const commonFieldDetails = commonFields.reduce((result, fieldKey) => {
			const fieldData = selectedSubcategories
				.map(subcategory => {
					const jsonFile = objectStore.jsonfiles.find(
						file => file.type && file.type.id === subcategory.id
					)
					return jsonFile && jsonFile.fields ? jsonFile.fields[fieldKey] : null
				})
				.find(Boolean) // Берем описание из первой подкатегории, где поле есть
			if (fieldData) result[fieldKey] = fieldData
			return result
		}, {})

		logger.log('🎀 commonFieldDetails:', commonFieldDetails)

		// Вместо перезаписи всего extraFilter, инициализируем только отсутствующие поля
		// Не изменяем существующие значения
		if (!this.extraFilter) {
			this.extraFilter = {}
		}

		// Только добавляем поля, которых еще нет в extraFilter
		Object.keys(commonFieldDetails).forEach(key => {
			if (!(key in this.extraFilter)) {
				this.extraFilter[key] = commonFieldDetails[key];
			}
		});
	}

	setActiveBoardId(v) {
		this.activeBoardId = v
	}

	// Функция для обновления колонки в локальном сторе
	updateColumnName = (board, columnId, newName) => {
		const column = this.getColumnById(board, columnId)
		if (column) {
			column.name = newName
		}
	}

	async updateTaskOrder(boardId, sourceColId, destColId, sourceIndex, destIndex) {
		const board = this.requests.find(item => item.boardId === boardId)
		const sourceColumn = board?.columns.find(column => column.columnId === sourceColId)
		const destinationColumn = board?.columns.find(column => column.columnId === destColId)
		const movedTask = sourceColumn?.requests[sourceIndex]
		if (!movedTask || !destinationColumn || (sourceColId === destColId && sourceIndex === destIndex)) return
		const requestId = movedTask.requestId
		const moveVersion = ++this.nextRequestMoveVersion
		const original = { columnId: sourceColId, index: sourceIndex }

		runInAction(() => {
			sourceColumn.requests.splice(sourceIndex, 1)
			const targetIndex = Math.max(0, Math.min(destIndex, destinationColumn.requests.length))
			destinationColumn.requests.splice(targetIndex, 0, movedTask)
			movedTask.columnId = destColId
			this.pendingRequestMoves.set(requestId, moveVersion)
		})

		try {
			const response = await apiClient.post(`/requests/${requestId}/move`, {
				targetColumnId: destColId,
				targetIndex: destIndex,
			})
			if (this.pendingRequestMoves.get(requestId) !== moveVersion) return
			runInAction(() => {
				const current = board.columns
					.map(column => ({ column, index: column.requests.findIndex(request => request.requestId === requestId) }))
					.find(location => location.index >= 0)
				if (current && response.data?.request) {
					Object.assign(current.column.requests[current.index], response.data.request, { requestId })
				}
				this.pendingRequestMoves.delete(requestId)
			})
		} catch (error) {
			if (this.pendingRequestMoves.get(requestId) === moveVersion) {
				runInAction(() => {
					const current = board.columns
						.map(column => ({ column, index: column.requests.findIndex(request => request.requestId === requestId) }))
						.find(location => location.index >= 0)
					if (current) {
						const [task] = current.column.requests.splice(current.index, 1)
						const restoreColumn = board.columns.find(column => column.columnId === original.columnId)
						if (task && restoreColumn) {
							restoreColumn.requests.splice(Math.min(original.index, restoreColumn.requests.length), 0, task)
							task.columnId = original.columnId
						}
					}
					this.pendingRequestMoves.delete(requestId)
					this.error = error.response?.data?.error || error.message
				})
			}
			throw error
		}
	}

	async closeRequest(requestId, outcome) {
		try {
			const response = await apiClient.post(`/requests/${requestId}/close`, { outcome })
			await this.fetchRequests('request-closed')
			return response.data.request
		} catch (error) {
			await this.fetchRequests('request-close-failed')
			throw error
		}
	}

	async reopenRequest(requestId, targetColumnId, targetIndex = null) {
		try {
			const response = await apiClient.post(`/requests/${requestId}/reopen`, { targetColumnId, targetIndex })
			await this.fetchRequests('request-reopened')
			return response.data.request
		} catch (error) {
			await this.fetchRequests('request-reopen-failed')
			throw error
		}
	}

	addRequestToLocalStore(boardId, columnId, requestData) {
		// Находим нужную доску
		const board = this.requests.find(board => board.boardId === boardId)
		if (!board) {
			logger.error(`Доска с boardId: ${boardId} не найдена`)
			return
		}

		// Находим нужную колонку
		const column = board.columns.find(col => col.columnId === columnId)
		if (!column) {
			logger.error(`Колонка с columnId: ${columnId} не найдена`)
			return
		}

		// Добавляем заявку в конец массива requests колонки
		column.requests.unshift({
			requestId: requestData.id, // Переименовываем id в requestId для соответствия структуре
			filter: requestData.filter,
			outcome: requestData.outcome,
			closedAt: requestData.closedAt,
			archivedAt: requestData.archivedAt,
			createdAt: requestData.createdAt,
			updatedAt: requestData.updatedAt,
			assigneeMembershipId: requestData.assigneeMembershipId,
			permissions: requestData.permissions,
			clientId: requestData.clientId,
			columnId: requestData.columnId,
			sort: requestData.sort,
			colorToken: requestData.colorToken,
			sourceId: requestData.sourceId,
			title: requestData.title,
			description: requestData.description,
		})

		logger.log(`Заявка добавлена в колонку ${columnId} на доске ${boardId}`)
	}

	// Функция для поиска всех досок по boardId
	getBoardsByIdArray = boardId => {
		const board = this.requests.find(board => board.boardId === boardId)
		return board ? board.columns : []
	}

	sortedColumns(board) {
		return (board?.columns || []).slice().sort((left, right) => left.sort - right.sort || left.columnId - right.columnId)
	}

	async updateColumnOrder(boardId, sourceIndex, destinationIndex) {
		const board = this.requests.find(board => board.boardId === boardId)
		const ordered = this.sortedColumns(board)
		if (!board || sourceIndex === destinationIndex || sourceIndex < 0 || destinationIndex < 0 || sourceIndex >= ordered.length || destinationIndex >= ordered.length) return
		const previousOrder = ordered.map(column => ({ column, sort: column.sort }))

		runInAction(() => {
			const [movedColumn] = ordered.splice(sourceIndex, 1)
			ordered.splice(destinationIndex, 0, movedColumn)
			ordered.forEach((column, index) => { column.sort = index + 1 })
			board.columns = ordered
		})

		try {
			await this.syncColumnOrderWithServer(boardId, ordered)
		} catch (error) {
			runInAction(() => {
				previousOrder.forEach(({ column, sort }) => { column.sort = sort })
				board.columns = previousOrder.map(({ column }) => column)
				this.error = error.response?.data?.error || error.message
			})
			await this.fetchRequests('column-order-error')
			throw error
		}
	}

	// Функция для поиска колонки по columnId внутри доски
	getColumnById = (board, columnId) => {
		return board.columns.find(column => column.columnId === columnId)
	}

	// не подходящий объект
	addToNotSuitedRequest = objectId => {
		// Если объект уже в списке "Не подходит" — удаляем его
		const index = this.notSuitedObjectForRequest.indexOf(objectId)
		if (index > -1) {
			logger.log(`Удаляем объект из списка "Не подходит": ${objectId}`)
			this.notSuitedObjectForRequest.splice(index, 1)
			return
		}

		// Если объект присутствует в списке "Подходит", удаляем его оттуда
		const indexInSuited = this.suitedObjectForRequest.indexOf(objectId)
		if (indexInSuited > -1) {
			this.suitedObjectForRequest.splice(indexInSuited, 1)
		}

		logger.log(`Добавляем объект в список "Не подходит": ${objectId}`)
		this.notSuitedObjectForRequest.push(objectId)
	}

	// подходящий объект
	addToSuitedRequest = objectId => {
		// Если объект уже в списке "Подходит" — удаляем его
		const index = this.suitedObjectForRequest.indexOf(objectId)
		if (index > -1) {
			logger.log(`Удаляем объект из списка "Подходит": ${objectId}`)
			this.suitedObjectForRequest.splice(index, 1)
			return
		}

		// Если объект присутствует в списке "Не подходит", удаляем его оттуда
		const indexInNotSuited = this.notSuitedObjectForRequest.indexOf(objectId)
		if (indexInNotSuited > -1) {
			this.notSuitedObjectForRequest.splice(indexInNotSuited, 1)
		}

		logger.log(`Добавляем объект в список "Подходит": ${objectId}`)
		this.suitedObjectForRequest.push(objectId)
	}

	// Функция для поиска доски по boardId
	getBoardById = boardId => {
		return this.requests.find(board => board.boardId === boardId)
	}


	async getObjectForRequest() {
		logger.log('Фильтр')
	}

	// Функция для загрузки доступных объектов с пагинацией
	async fetchAvailableObjects(requestId, page = 1, limit = 30) {
		logger.log('🎈 Загрузка доступных объектов для заявки с пагинацией')
		logger.log(`Получение доступных объектов для заявки: requestId=${requestId}, page=${page}, limit=${limit}`)

		try {
			const response = await apiClient.get(`/requests/${requestId}/available-objects`, {
				params: { page, limit }
			})

			// Нормализуем данные перед записью в store
			const normalizedAvailableObjects = (response.data.objects || []).map(normalizeLinkedObject)

			runInAction(() => {
				this.activeRequestObjectsAvailable = normalizedAvailableObjects
				this.currentPageAvailable = page
				this.totalPagesAvailable = Math.ceil(response.data.totalCount / limit)
			})

			return {
				objects: normalizedAvailableObjects,
				totalCount: response.data.totalCount,
				page
			}
		} catch (error) {
			logger.error('Ошибка при получении доступных объектов заявки:', error)
			runInAction(() => {
				this.activeRequestObjectsAvailable = []
				this.currentPageAvailable = 1
				this.totalPagesAvailable = 1
			})
			return {
				objects: [],
				totalCount: 0,
				page
			}
		}
	}

	// Метод для обновления деталей заявки (title, description)
	async updateRequestDetails(requestId, boardId, columnId, updates, countryCodeISO) {
		logger.log('🎈 ENDP-23234423 - updateRequestDetails'); // TODO: Assign proper endpoint number
		logger.log(`Updating request ${requestId} details:`, { updates, countryCodeISO });

		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			const payload = {
				...updates, // { title: 'new title' } or { description: 'new desc' }
				boardId,    // Включаем boardId и columnId, как запрошено
				columnId,
				countryCodeISO // Добавляем ISO код страны
			};
			logger.log('Sending update payload:', payload);

			// Используем PATCH, так как обновляем частично
			const response = await apiClient.patch(`/requests/${requestId}/details`, payload);

			// Backend возвращает canonical placement и source display из owner-scoped PostgreSQL query.
			const updatedData = response.data;
			logger.log('Request details updated successfully on server:', updatedData);

			// Обновляем локальный стор
			runInAction(() => {
				const board = this.requests.find(b => b.boardId === updatedData.boardId);
				if (board) {
					const column = board.columns.find(c => c.columnId === updatedData.columnId);
					if (column) {
						const request = column.requests.find(r => r.requestId === updatedData.requestId);
						if (request) {
							// Обновляем только те поля, которые были в ответе (title/description)
							if (updatedData.title !== undefined) {
								request.title = updatedData.title;
							}
							if (updatedData.description !== undefined) {
								request.description = updatedData.description;
							}
							if (updatedData.sourceId !== undefined) {
								request.sourceId = updatedData.sourceId;
								request.sourceName = updatedData.sourceName || null;
							}
							// Можно также обновить updatedAt, если сервер его возвращает и он есть в модели заявки в сторе
							// request.updatedAt = updatedData.updatedAt; 
							logger.log(`Local request ${request.requestId} updated in store.`);
						} else {
							logger.warn(`Request ${updatedData.requestId} not found in column ${updatedData.columnId} in local store.`);
						}
					} else {
						logger.warn(`Column ${updatedData.columnId} not found in board ${updatedData.boardId} in local store.`);
					}
				} else {
					logger.warn(`Board ${updatedData.boardId} not found in local store.`);
				}
			});

			runInAction(() => {
				this.loading = false;
			});

			logger.log('🎈updatedData:', updatedData);
			return updatedData; // Возвращаем обновленные данные


		} catch (error) {
			const errorMsg = error.response?.data?.message || error.message || 'Ошибка при обновлении заявки';
			logger.error('Error updating request details:', error);
			runInAction(() => {
				this.error = errorMsg;
				this.loading = false;
			});
			throw new Error(errorMsg); // Пробрасываем ошибку для обработки в компоненте
		}
	}

	async updateRequestCriteria(requestId, criteria) {
		const response = await apiClient.patch(`/requests/${requestId}/criteria`, criteria)
		const updated = response.data
		const request = updated.request
		runInAction(() => {
			for (const board of this.requests) {
				for (const column of board?.columns || []) {
					const current = column?.requests?.find(item => String(item.requestId) === String(requestId))
					if (current && request) {
						current.filter = request.filter
						current.countryCodeISO = request.countryCodeISO
						current.updatedAt = request.updatedAt
					}
				}
			}
		})
		return updated
	}

	async updateRequestClient(requestId, clientId) {
		const response = await apiClient.put(`/requests/${requestId}/client`, { clientId: clientId || null })
		const updated = response.data
		runInAction(() => {
			for (const board of this.requests) {
				for (const column of board?.columns || []) {
					const request = column?.requests?.find(item => String(item.requestId) === String(requestId))
					if (request) {
						request.clientId = updated.clientId
						request.updatedAt = updated.updatedAt
						return
					}
				}
			}
		})
		return updated
	}

	async fetchRequestDetail(requestId) {
		const response = await apiClient.get(`/requests/${requestId}/detail`)
		return response.data?.request || null
	}

	// Метод для восстановления заявки из архива
	async restoreRequest(requestId) {
		logger.log(`🎈 restoreRequest - Восстанавливаем заявку ${requestId}`);
		
		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// Отправляем PATCH-запрос на эндпоинт
			const response = await apiClient.patch(`/requests/${requestId}/restore`);

			logger.log(`Заявка ${requestId} успешно восстановлена:`, response.data);

			// После успешного восстановления запрашиваем обновленные данные
			// Это позволит обновить UI с новой заявкой в правильной колонке
			await this.fetchRequests('restore');

			return response.data;
		} catch (error) {
			const errorMsg = error.response?.data?.message || error.message || 'Ошибка при восстановлении заявки';
			logger.error('Error restoring request:', error);
			runInAction(() => {
				this.error = errorMsg;
			});
			throw new Error(errorMsg); // Пробрасываем ошибку для обработки в компоненте
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	// Метод для архивирования заявки
	async archiveRequest(requestId) {
		logger.log(`🎈 ENDP-XX - archiveRequest - Архивируем заявку ${requestId}`); // TODO: Assign proper endpoint number

		runInAction(() => {
			this.loading = true;
			this.error = null;
		});

		try {
			// Отправляем PATCH-запрос на новый эндпоинт
			const response = await apiClient.patch(`/requests/${requestId}/archive`);

			logger.log(`Заявка ${requestId} успешно архивирована на сервере:`, response.data);

			// Удаляем заявку из локального стора после успешного архивирования
			runInAction(() => {
				let requestFoundAndRemoved = false;
				this.requests = this.requests.map(board => {
					// Клонируем доску, чтобы избежать мутации
					const updatedBoard = { ...board };
					updatedBoard.columns = board.columns.map(column => {
						// Клонируем колонку
						const updatedColumn = { ...column };
						const initialRequestCount = updatedColumn.requests.length;
						// Фильтруем заявки, удаляя архивированную
						updatedColumn.requests = updatedColumn.requests.filter(req => req.requestId !== requestId);
						if (updatedColumn.requests.length < initialRequestCount) {
							requestFoundAndRemoved = true;
							// Обновляем сортировку оставшихся заявок в этой колонке
							updatedColumn.requests.forEach((req, index) => {
								req.sort = index + 1;
							});
						}
						return updatedColumn;
					});
					return updatedBoard;
				});

				if (requestFoundAndRemoved) {
					logger.log(`Заявка ${requestId} удалена из локального стора.`);
				} else {
					logger.warn(`Заявка ${requestId} не найдена в локальном сторе для удаления.`);
				}
			});

		} catch (error) {
			const errorMsg = error.response?.data?.message || error.message || 'Ошибка при архивировании заявки';
			logger.error('Error archiving request:', error);
			runInAction(() => {
				this.error = errorMsg;
			});
			throw new Error(errorMsg); // Пробрасываем ошибку для обработки в компоненте
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

}

const requestStore = new RequestStore()
registerTenantScopedReset('requests-and-kanban', () => requestStore.clearOrganizationState())
export default requestStore
